import React from 'react';
import cx from 'classnames';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import ArrowButton from './ResponseSection/ArrowButton';
import { useTranslation } from 'react-i18next';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { computeTextColor } from '@vimily/bonjoro-ui-components-v2';

export enum ProceedButton {
  Next = 'next',
  Arrow = 'arrow',
  ManualUpload = 'manual-upload',
  Scroll = 'scroll',
}

export interface QuestionSectionProps {
  heading: React.ReactNode;
  question: React.ReactNode;
  minHeight?: string;
  minWidth?: string;
  previousDisabled?: boolean;
  nextDisabled?: boolean;
  onNextClick?: (button: ProceedButton) => void;
  onPreviousClick?: () => void;
  disabled?: boolean;
  className?: string;
  desktopView?: boolean;
  ctaColor?: string;
  ctaCornerRadius?: number;
  fontFamily?: string;
}

const Container = styled.div<
  Pick<QuestionSectionProps, 'minHeight' | 'minWidth'>
>`
  ${({ minHeight, minWidth }) => `
    min-height: ${minHeight};
    min-width: ${minWidth};
  `}

  @media (max-width: 768px) {
    min-width: unset;
    min-height: unset;
  }
`;

export default function QuestionSection({
  heading,
  question,
  minHeight = '449px',
  minWidth = '409px',
  previousDisabled = false,
  nextDisabled = false,
  onNextClick,
  onPreviousClick,
  className,
  desktopView = false,
  ctaColor,
  ctaCornerRadius = 0,
  fontFamily
}: QuestionSectionProps) {
  const responsiveClass =
    'absolute md:relative bg-black/50 md:bg-transparent py-2.5 px-4 md:p-0';
  const desktopOnlyClass = 'relative bg-transparent p-0';

  const { t } = useTranslation();

  const buttonProps = {
    className: 'ml-auto px-4 !h-10 !py-1.5 w-auto md:min-w-28 md:w-auto text-sm',
    variant: ButtonVariant.Primary,
    onClick: () => onNextClick?.(ProceedButton.Next),
    disabled: nextDisabled,
    style: {
      borderRadius: `${ctaCornerRadius}px`,
      borderColor: ctaColor,
      backgroundColor: ctaColor,
      color: computeTextColor(ctaColor),
      fontFamily
    }
  };

  return (
    <Container
      className={cx(
        'flex-none md:flex-1 px-4 py-2 xs:p-8 flex flex-col',
        className,
        desktopView ? 'max-w-[50%]' : 'md:max-w-[50%] '
      )}
      minHeight={minHeight}
      minWidth={minWidth}
    >
      {heading}
      {question}
      <div
        className={cx(
          'hidden z-10 bottom-0 left-0 w-full md:!flex mt-auto',
          desktopView ? desktopOnlyClass : responsiveClass
        )}
      >
        <ArrowButton
          className='rounded-r-none'
          disabled={previousDisabled}
          onClick={onPreviousClick}
        >
          <FontAwesomeIcon icon={faChevronUp} />
        </ArrowButton>
        <ArrowButton
          className='rounded-l-none'
          disabled={nextDisabled}
          onClick={() => onNextClick?.(ProceedButton.Arrow)}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </ArrowButton>

        <Button {...buttonProps}>
          {t('testimonial:response.next')}
        </Button>
      </div>
    </Container>
  );
}
